// src/people/PersonModal.tsx
//
// BekalNanti — Milestone P3.6: Add / Edit Person Modal
// Supports adding multiple people with same relationship, custom labels, and non-destructive archiving.

import React, { useState, useEffect } from 'react'
import { PersonRecord } from '../journey/types'
import {
  CANONICAL_RELATIONSHIPS,
  addPerson,
  updatePerson,
  archivePerson,
  unarchivePerson,
  isPersonSelf,
} from '../lib/peopleService'
import { X, UserPlus, UserCheck, AlertCircle, Loader2, Archive, RotateCcw } from 'lucide-react'

interface PersonModalProps {
  isOpen: boolean
  householdId: string
  personToEdit?: PersonRecord | null
  onClose: () => void
  onSuccess: () => Promise<void>
}

export const PersonModal: React.FC<PersonModalProps> = ({
  isOpen,
  householdId,
  personToEdit,
  onClose,
  onSuccess,
}) => {
  const isEditing = Boolean(personToEdit)
  const isSelf = personToEdit ? isPersonSelf(personToEdit) : false

  const [fullName, setFullName] = useState('')
  const [relationship, setRelationship] = useState('child')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [loading, setLoading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [confirmArchive, setConfirmArchive] = useState(false)

  useEffect(() => {
    if (personToEdit) {
      setFullName(personToEdit.fullName)
      setRelationship(personToEdit.relationship)
      setDateOfBirth(personToEdit.dateOfBirth || '')
    } else {
      setFullName('')
      setRelationship('child')
      setDateOfBirth('')
    }
    setErrorMsg(null)
    setConfirmArchive(false)
  }, [personToEdit, isOpen])

  if (!isOpen) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!fullName.trim()) {
      setErrorMsg('Nama lengkap tidak boleh kosong.')
      return
    }

    setLoading(true)
    setErrorMsg(null)

    try {
      if (isEditing && personToEdit) {
        await updatePerson(personToEdit.id, {
          fullName: fullName.trim(),
          relationship: isSelf ? 'self' : relationship,
          dateOfBirth: dateOfBirth ? dateOfBirth : null,
        })
      } else {
        await addPerson(householdId, {
          fullName: fullName.trim(),
          relationship,
          dateOfBirth: dateOfBirth ? dateOfBirth : null,
        })
      }

      await onSuccess()
      onClose()
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal menyimpan data orang.')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleArchive = async () => {
    if (!personToEdit) return
    setLoading(true)
    setErrorMsg(null)

    try {
      if (personToEdit.status === 'archived') {
        await unarchivePerson(personToEdit.id)
      } else {
        await archivePerson(personToEdit.id)
      }
      await onSuccess()
      onClose()
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal mengubah status arsip.')
    } finally {
      setLoading(false)
    }
  }

  // Filter out 'self' when adding new person since self is already established
  const selectableRelationships = isSelf
    ? CANONICAL_RELATIONSHIPS.filter((r) => r.value === 'self')
    : CANONICAL_RELATIONSHIPS.filter((r) => r.value !== 'self')

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="person-modal-title"
    >
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl border border-slate-200/90 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-teal-50 text-[#0EA5A4] flex items-center justify-center">
              {isEditing ? <UserCheck size={18} /> : <UserPlus size={18} />}
            </div>
            <div>
              <h2 id="person-modal-title" className="text-base font-bold text-slate-900 leading-tight">
                {isEditing ? 'Ubah Data Orang' : 'Tambah Orang / Keluarga'}
              </h2>
              <p className="text-[11px] text-slate-500">
                {isEditing
                  ? 'Perbarui informasi orang yang dikelola'
                  : 'Daftarkan penerima bekal atau anggota keluarga baru'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            aria-label="Tutup"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 text-left">
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs flex items-center gap-2">
              <AlertCircle size={14} className="flex-shrink-0" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Full Name */}
          <div className="space-y-1.5">
            <label htmlFor="person-fullname-input" className="text-xs font-bold text-slate-700">
              Nama Lengkap / Panggilan
            </label>
            <input
              id="person-fullname-input"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Contoh: Kayla, Ayu, Ayah, dll."
              required
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            />
          </div>

          {/* Relationship Selection */}
          <div className="space-y-1.5">
            <label htmlFor="person-relationship-select" className="text-xs font-bold text-slate-700">
              Hubungan Keluarga
            </label>
            {isSelf ? (
              <div className="px-3 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-600 font-medium">
                Saya (Pembuat &amp; Pengelola Akun)
              </div>
            ) : (
              <select
                id="person-relationship-select"
                value={relationship}
                onChange={(e) => setRelationship(e.target.value)}
                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
              >
                {selectableRelationships.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label} ({opt.englishLabel})
                  </option>
                ))}
              </select>
            )}
            {/* Relationship Change Warning (Section G4) */}
            {isEditing && !isSelf && personToEdit && relationship !== personToEdit.relationship && (
              <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
                Perubahan hubungan keluarga akan langsung tercermin pada seluruh rencana yang terkait dengan orang ini.
              </div>
            )}
            <p className="text-[11px] text-slate-400">
              Anda dapat mendaftarkan beberapa orang dengan hubungan yang sama (misal: Anak ke-1, Anak ke-2, dll).
            </p>
          </div>

          {/* Date of Birth (Optional) */}
          <div className="space-y-1.5">
            <label htmlFor="person-dob-input" className="text-xs font-bold text-slate-700">
              Tanggal Lahir <span className="text-slate-400 font-normal">(Opsional)</span>
            </label>
            <input
              id="person-dob-input"
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              className="w-full px-3 py-2.5 rounded-xl border border-slate-200 bg-white text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#0EA5A4]"
            />
            <p className="text-[11px] text-slate-400">
              Membantu menghitung estimasi tahun mulai jenjang pendidikan anak secara otomatis.
            </p>
          </div>

          {/* Archive / Restore Option (for existing non-self persons) - Section G1 */}
          {isEditing && !isSelf && personToEdit && (
            <div className="pt-3 border-t border-slate-100 space-y-2">
              {confirmArchive ? (
                <div className="p-3.5 rounded-2xl bg-amber-50/90 border border-amber-200 text-xs space-y-2.5 text-amber-900">
                  <div className="space-y-1">
                    <p className="font-bold text-slate-900">
                      {personToEdit.status === 'archived'
                        ? 'Pulihkan orang ini ke daftar aktif?'
                        : 'Arsipkan orang ini?'}
                    </p>
                    <p className="text-[11px] text-slate-600 leading-relaxed">
                      Orang ini akan disembunyikan dari daftar orang yang sedang aktif Anda kelola. Data dan rencana yang sudah dibuat tetap tersimpan aman dan dapat dipulihkan kapan saja.
                    </p>
                  </div>

                  {personToEdit.status !== 'archived' && (
                    <div className="p-2.5 rounded-xl bg-white/80 border border-amber-200/70 text-[11px] text-slate-600 space-y-0.5">
                      <p className="font-semibold text-slate-800">Salah memasukkan data orang ini?</p>
                      <p>
                        Jika orangnya sebenarnya sama dan hanya nama atau hubungan yang keliru, Anda tidak perlu mengarsipkannya. Cukup ubah datanya langsung pada formulir ini.
                      </p>
                    </div>
                  )}

                  <div className="flex items-center gap-2 pt-1 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setConfirmArchive(false)}
                      className="px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-xs font-semibold cursor-pointer hover:bg-slate-50"
                    >
                      Batal
                    </button>
                    {personToEdit.status !== 'archived' && (
                      <button
                        type="button"
                        onClick={() => setConfirmArchive(false)}
                        className="px-3 py-1.5 rounded-xl bg-teal-50 border border-teal-200 text-[#0F766E] text-xs font-semibold cursor-pointer hover:bg-teal-100"
                      >
                        Edit Data Orang
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={handleToggleArchive}
                      disabled={loading}
                      className="px-3 py-1.5 rounded-xl bg-amber-600 hover:bg-amber-700 text-white font-bold text-xs cursor-pointer shadow-2xs"
                    >
                      {personToEdit.status === 'archived' ? 'Ya, Pulihkan' : 'Ya, Arsipkan'}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirmArchive(true)}
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-amber-700 cursor-pointer"
                >
                  {personToEdit.status === 'archived' ? (
                    <>
                      <RotateCcw size={13} />
                      <span>Pulihkan dari Arsip</span>
                    </>
                  ) : (
                    <>
                      <Archive size={13} />
                      <span>Arsipkan Orang Ini</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-3 border-t border-slate-100 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-xs font-semibold cursor-pointer"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white text-xs font-bold shadow-2xs transition-all cursor-pointer disabled:opacity-50"
            >
              {loading && <Loader2 size={13} className="animate-spin" />}
              <span>{isEditing ? 'Simpan Perubahan' : 'Tambah Orang'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
